import _ from 'lodash';
import axios from 'axios';
import 'moment/locale/es-mx';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  placeHolders,
  roleConst,
  timeFormatComplete,
} from '../../../constants';
import { lockConst } from '../../../constants/Lock.const';
import {
  dateFormat,
  timeFormat12,
  fullDate,
} from '../../../constants/TimeConst';
import { addTime } from '../../../util/addTime';
import { arraySort } from '../../../util/arraySort';
import { CustomSelect } from '../../uiComponents';
import { Loading } from '../Loading';
import { LockModalBody } from '../Lock/LockModalBody';
import { MyLocksModalBody } from '../Lock/MyLocksModalBody';
import { AgendaAppointment } from './AgendaAppointment';
import { AppointmentExtension } from './AppointmentExtension';
import { HeaderItem } from './HeaderItem';
import { SiderAgenda } from './SiderAgenda';
import './styles.css';
import { Button, Layout, Modal, Space, Table } from 'antd';
import { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import 'moment/locale/es-mx';
import {
  clearAppointmentById,
  clearMissedAppointments,
  getAllSubsidiaries,
  getCabainTypes,
  getSubsidiaries,
  hideLoading,
  setSelectedBranchId,
  showLoading,
} from '../../../appRedux/actions';
import { setAppointmentData } from '../../../appRedux/actions/General';
import {
  ClockCircleOutlined,
  LockOutlined,
  PlusOutlined,
  StopOutlined,
  RightOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import { stringDate, stringTimeToNumber } from '../../../util';
import { featureFlags } from '../../../util/featureFlags';
import { NumberAppointment } from './NumberAppointment';

const { Sider } = Layout;

const branchRestrictedAgendaRoles = [
  roleConst.branchManager,
  roleConst.franchisee,
  roleConst.medic,
  roleConst.cosmetologist,
];

export const CalendarAgenda = ({ dairyPrivileges, blockersPrivileges }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { Content } = Layout;
  const { cabainTypes } = useSelector((state) => state.cabainType);
  const { appointmentData } = useSelector((state) => state.generalStates);
  const [currentDate, setCurrentDate] = useState(() =>
    appointmentData?.date ? moment(appointmentData?.date) : moment(),
  );
  const [child, setChild] = useState(null);
  const { allSubsidiaries, subsidiaries } = useSelector(
    (state) => state.subsidiary,
  );
  const { locks } = useSelector((state) => state.lock);
  const { appointments, selectedBranchId } = useSelector(
    (state) => state.agenda,
  );
  const [branches, setBranches] = useState(null);
  const [modalTitle, setModalTitle] = useState(null);
  const [fixedData, setFixedData] = useState([]);
  const [cabains, setCabains] = useState([]);
  const [fixedColumns, setFixedColumns] = useState([]);
  const [selectedCabainId, setSelectedCabainId] = useState();
  const [selectedTime, setSelectedTime] = useState();
  const timeIterval = '00:30:00';
  const { role, branchId: loggedBranchId } = useSelector(
    (state) => state.userAuth,
  );
  const [openLocks, setOpenLocks] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const agendaRequestSource = useRef(null);
  const hasBranchRestrictedAgenda = branchRestrictedAgendaRoles.includes(role);
  const agendaBranchId = hasBranchRestrictedAgenda
    ? loggedBranchId
    : selectedBranchId;

  useEffect(() => {
    dispatch(getCabainTypes());
    dispatch(getAllSubsidiaries());
    dispatch(clearMissedAppointments());
    dispatch(clearAppointmentById());
  }, [dispatch]);

  useEffect(() => {
    if (!agendaBranchId || openLocks) return;

    const requestTimeout = setTimeout(() => {
      const requestSource = axios.CancelToken.source();
      agendaRequestSource.current = requestSource;
      setIsLoading(true);
      dispatch(
        getSubsidiaries(
          {
            status: '1',
            agenda: '1',
            date: currentDate,
            withDeleted: false,
            branchId: agendaBranchId,
            excludeCabainLocks: 1,
          },
          { cancelToken: requestSource.token },
        ),
      ).finally(() => {
        if (agendaRequestSource.current === requestSource) {
          agendaRequestSource.current = null;
          setIsLoading(false);
        }
      });
    }, 300);

    return () => {
      clearTimeout(requestTimeout);
      if (agendaRequestSource.current) {
        agendaRequestSource.current.cancel(
          'Agenda request replaced by a newer selection',
        );
        agendaRequestSource.current = null;
      }
      setIsLoading(false);
    };
  }, [dispatch, locks, appointments, currentDate, agendaBranchId, openLocks]);

  useEffect(() => {
    setBranches(
      subsidiaries.length > 0
        ? subsidiaries.reduce((obj, item) => ({ ...obj, [item.id]: item }), {})
        : [],
    );
  }, [subsidiaries]);

  useEffect(() => {
    if (hasBranchRestrictedAgenda) {
      if (loggedBranchId && selectedBranchId !== loggedBranchId) {
        dispatch(setSelectedBranchId(loggedBranchId));
      }
      return;
    }

    if (!selectedBranchId && allSubsidiaries.length > 0) {
      dispatch(setSelectedBranchId(allSubsidiaries[0].id));
    }
  }, [
    allSubsidiaries,
    hasBranchRestrictedAgenda,
    loggedBranchId,
    selectedBranchId,
    dispatch,
  ]);

  useEffect(() => {
    if (selectedBranchId) {
      dispatch(
        setAppointmentData({
          branchId: selectedBranchId,
          cabainId: selectedCabainId,
          time: selectedTime,
        }),
      );
    }
  }, [selectedBranchId, dispatch, selectedCabainId, selectedTime]);

  useEffect(() => {
    if (branches && selectedBranchId) {
      dispatch(showLoading());
      const branch = branches[selectedBranchId];
      setCabains(branch?.cabain || []);
      let cabains = _.cloneDeep(branch?.cabain);
      let cabainsOriginal = _.cloneDeep(branch?.cabain);
      const selectedDate = moment(currentDate).format(dateFormat);
      let lockByDay = false;

      cabains = cabains?.map((cabain) => {
        cabain.appointments = cabain.appointments.reduce(
          (obj, item) => ({ ...obj, [`${item.startTime}-${item.date}`]: item }),
          {},
        );

        lockByDay = branch?.locks?.find(
          (lock) => lock?.type === lockConst.day && lock?.date === selectedDate,
        );

        const locks = {
          [lockConst.hour]: [],
          [lockConst.cabain]: [],
          [lockConst.hoursRange]: [],
          [lockConst.daysRange]: [],
        };
        const timeLocksArray = [];

        const [cabainLocks] = branch?.locks
          .filter((item) => item?.cabain?.id === cabain.id)
          .map((item) => {
            const { type, time, days } = item;
            const date = item?.recurrent
              ? selectedDate
              : moment(item?.date).format(dateFormat);
            switch (type) {
              case lockConst.hour:
                timeLocksArray.push(...time);
                locks[lockConst.hour] = [
                  ...locks[lockConst.hour],
                  ...timeLocksArray.map((t) => {
                    return `${t}-${date}`;
                  }),
                ];
                return locks;
              case lockConst.cabain:
                locks[lockConst.cabain].push(
                  item?.recurrent ? selectedDate : item?.date,
                );
                return locks;
              case lockConst.hoursRange:
                locks[lockConst.hoursRange] = [
                  ...locks[lockConst.hoursRange],
                  time.map((t) => {
                    const selectedDay = moment(selectedDate).day().toString();
                    if (days?.includes(selectedDay)) {
                      return `${t}-${date}`;
                    }
                    return null;
                  }),
                ];
                return locks;
              case lockConst.daysRange:
                locks[lockConst.daysRange].push(time.map((t) => t));
                return locks;
              default:
                return [];
            }
          });
        cabain.locks = cabainLocks;
        return cabain;
      });

      /* Getting datOfWeek from the selected date and get schedule */
      const branchSchedule = branch?.schedules.find(
        (schedule) => schedule.day === currentDate.isoWeekday() - 1,
      );
      let fixedData = [];

      if (branchSchedule) {
        const branchOpening = parseInt(branchSchedule.opening) || 0; // 0
        const branchClosing = parseInt(branchSchedule.closing) || 0; // 0
        const branchIsClosed = !branchOpening && !branchClosing; // 0
        let startTime = branchIsClosed ? 0 : branchOpening; // 0
        const closeTime = branchIsClosed ? 24 : branchClosing; // 20
        let currentTime = branchSchedule.opening; // '07:00:00'
        const currentAppointment = [];

        for (startTime; startTime <= closeTime - 0.5; startTime += 0.5) {
          const startTimeObj = {
            time: moment(
              `${startTime} ${
                startTime - Math.floor(startTime) !== 0 ? ':30:00' : ':00:00'
              } `,
              [timeFormatComplete],
            ).format(timeFormat12),
            key: Math.random(),
          };

          if (!cabains) break;
          const cabainsSorted = arraySort(cabains, 'name');
          for (const cabain of cabainsSorted) {
            const { appointments, locks } = cabain;
            const indexTime = `${moment(currentTime, [
              timeFormatComplete,
            ]).format(timeFormatComplete)}-${selectedDate}`;
            let component = <></>;
            const cabainSchedule = cabain?.schedules?.find(
              (schedule) => schedule.day === currentDate.isoWeekday() - 1,
            );

            const cabainOpening = stringTimeToNumber(cabainSchedule?.opening);
            const cabainClosing = stringTimeToNumber(cabainSchedule?.closing);
            const [currentAppointmentTime] = indexTime.split('-');
            const daysOff = [
              branchOpening,
              branchClosing,
              cabainOpening,
              cabainClosing,
            ];

            // Render add appointment button
            component = dairyPrivileges.includes('create') ? (
              <Button
                type="link"
                onClick={() => {
                  setSelectedCabainId(cabain?.id);
                  setSelectedTime(currentAppointmentTime);
                  setTimeout(() => navigate(`/dashboard/agenda/crear/`), 500);
                }}
              >
                <PlusOutlined className="add" />
              </Button>
            ) : (
              <>
                <StopOutlined className="forbidden" />
              </>
            );

            if (locks?.hour?.includes(indexTime)) {
              // Render locks by hour
              component = <LockOutlined className="locked" />;
            }
            if (locks?.cabain && locks?.cabain?.includes(selectedDate)) {
              // Render locks by cabain
              component = <LockOutlined className="locked" />;
            }
            if (lockByDay) {
              // Render locks by day
              component = <LockOutlined className="locked" />;
            }
            if (
              locks?.hours_range &&
              locks.hours_range.some(
                (lock) =>
                  moment(lock[0], timeFormatComplete) <=
                    moment(indexTime, timeFormatComplete) &&
                  moment(lock[1], timeFormatComplete) >
                    moment(indexTime, timeFormatComplete),
              )
            ) {
              // Render locks by hours range
              component = <LockOutlined className="locked" />;
            }
            if (locks?.days_range) {
              const indexDate = moment(
                `${selectedDate}T${indexTime}`,
                fullDate,
              );
              if (
                locks.days_range.some(
                  (lock) =>
                    moment(`${lock[0]}T00:00:00`, fullDate) <= indexDate &&
                    indexDate <= moment(`${lock[1]}T23:59:59`, fullDate),
                )
              ) {
                component = <LockOutlined className="locked" />;
              }
              // Render locks by days range
            }
            if (daysOff.includes(0)) {
              // Render locks by non-business days
              component = <LockOutlined className="locked" />;
            }
            if (
              cabainOpening > startTime ||
              cabainClosing <= startTime ||
              currentTime > startTime ||
              (moment(`${selectedDate} ${currentTime}`).isBefore(
                moment().subtract(30, 'minutes'),
              ) &&
                cabainOpening <= stringTimeToNumber(currentTime))
            ) {
              // Render locks by schedules and past dates

              component = <LockOutlined className="locked" />;
            }
            if (appointments[indexTime]) {
              const [time, year, month, day] = indexTime.split('-');
              const date = `${year}-${month}-${day}`;
              //Conteo de las citas
              const numberAppointment =
                cabainsOriginal
                  ?.find((c) => c.id === cabain.id)
                  ?.appointments?.filter(
                    (item) => item.startTime === time && item.date === date,
                  ) || [];

              const isMultiple = numberAppointment.length > 1;
              // Render date component
              currentAppointment.push({
                ...appointments[indexTime],
                cabain,
                indexTime,
                isMultiple,
              });

              component = featureFlags.externalAppointment ? (
                <NumberAppointment
                  appointments={numberAppointment}
                  privileges={dairyPrivileges}
                  cabain={cabain}
                  indexTime={indexTime}
                />
              ) : (
                <AgendaAppointment
                  appointment={appointments[indexTime]}
                  indexTime={
                    featureFlags.externalAppointment ? indexTime : null
                  }
                  cabain={featureFlags.externalAppointment ? cabain : null}
                  privileges={dairyPrivileges}
                />
              );
            }

            for (let i = 0; i < currentAppointment.length; i++) {
              const currentTime = moment(
                indexTime.split('-')[0],
                timeFormatComplete,
              );
              if (
                moment(currentAppointment[i]?.startTime, timeFormatComplete) <=
                  currentTime &&
                moment(currentAppointment[i]?.endTime, timeFormatComplete) >
                  currentTime &&
                cabain?.id === currentAppointment[i]?.cabain?.id &&
                currentAppointment[i]?.indexTime !== indexTime
              ) {
                component = (
                  <AppointmentExtension
                    appointment={currentAppointment[i]}
                    isMultiple={currentAppointment[i]?.isMultiple}
                  />
                );
              }
            }
            startTimeObj[cabain?.name] = component;
          }
          fixedData.push(startTimeObj);
          currentTime = addTime(currentTime, timeIterval);
        }
        setFixedData(fixedData);
      }
      dispatch(hideLoading());
    }
  }, [selectedBranchId, branches, currentDate, navigate, dispatch]);

  useEffect(() => {
    if (fixedData && cabains) {
      const sortedCabains = cabains
        ?.sort((a, b) => (a.name > b.name ? 1 : -1))
        .sort(
          (firstItem, secondItem) =>
            firstItem.type?.order - secondItem.type?.order,
        );
      const colors = { default: '#E5E5E5' };

      cabainTypes?.forEach(
        (cabainType) => (colors[cabainType.name] = cabainType.color.color),
      );

      const columns = [
        {
          title: <ClockCircleOutlined />,
          dataIndex: 'time',
          fixed: true,
          className: 'agenda-time-cell',
        },
        ...sortedCabains.map(({ description, name, type }, index) => {
          return {
            key: index,
            title: (
              <HeaderItem
                description={description}
                cabain={name}
                color={colors[type?.name || 'default']}
              />
            ),
            dataIndex: name,
            className: 'agenda-slot-cell',
          };
        }),
      ];
      setFixedColumns(columns);
    }
  }, [cabainTypes, cabains, fixedData]);

  const getModal = (action = null) => {
    setOpenLocks(true);

    switch (action) {
      case 'locks':
        setModalTitle(lockConst.lockLabel);
        setChild(<LockModalBody setOpenLocks={setOpenLocks} />);
        break;
      case 'myLocks':
        setModalTitle(lockConst.locksLabel);
        setChild(<MyLocksModalBody privileges={blockersPrivileges} />);
        break;
      default:
        break;
    }
  };

  const sizeTable = (fixedColumns.length - 1) * 190 + 100;
  return (
    <>
      {isLoading && <Loading isLoading={isLoading} />}
      <Layout
        className="layout-agenda"
        style={{ height: 'calc(100vh - 280px)' }}
      >
        <Layout style={{ height: '100%' }}>
          <Content style={{ height: '100%' }}>
            <div
              className="container"
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Space
                style={{
                  display: 'flex',
                  marginBottom: 8,
                  justifyContent: 'left',
                }}
                align="start"
              >
                <Button
                  type="ghost"
                  className="btn-primary-outline"
                  onClick={() => {
                    navigate('/dashboard/citas');
                  }}
                >
                  Citas
                </Button>
                {!hasBranchRestrictedAgenda && (
                  <CustomSelect
                    placeholder={placeHolders.branch}
                    style={{ minWidth: '200px' }}
                    onChange={(id) => {
                      dispatch(setSelectedBranchId(id));
                    }}
                    options={allSubsidiaries?.map(({ id, name }) => ({
                      value: id,
                      label: name,
                    }))}
                    value={selectedBranchId}
                  ></CustomSelect>
                )}
                <Button onClick={() => getModal('myLocks')}>
                  <LockOutlined className="locked" />
                  {lockConst.locksLabel}
                </Button>
                {blockersPrivileges.includes('create') && (
                  <Button type="primary" onClick={() => getModal('locks')}>
                    <LockOutlined className="locked" />
                    {lockConst.lockLabel}
                  </Button>
                )}
              </Space>
              <h3
                style={{
                  textAlign: 'right',
                  marginBottom: '20px',
                  marginRight: '10px',
                }}
              >
                {stringDate(currentDate)}
              </h3>
              <Table
                className="agenda"
                style={{
                  width: '100%',
                  flex: 1,
                  height: '100%',
                }}
                columns={fixedColumns}
                dataSource={fixedData}
                pagination={false}
                width={sizeTable}
                scroll={{
                  x: 'max-content',
                  y: 'calc(100vh - 465px)',
                }}
                loading={isLoading}
              />
            </div>
          </Content>
          <Sider
            width={collapsed ? 0 : 200}
            style={{ background: '#fff', borderLeft: '1px solid #eee' }}
            collapsible
            collapsed={collapsed}
            onCollapse={setCollapsed}
            trigger={null}
          >
            <div
              style={{
                transition: 'right 0.3s',
                width: 40,
                marginLeft: 'auto',
              }}
            >
              <Button
                icon={collapsed ? <CalendarOutlined /> : <RightOutlined />}
                onClick={() => setCollapsed(!collapsed)}
                style={{ width: '100%' }}
              />
            </div>
            <div hidden={collapsed} style={{ marginTop: 40 }}>
              <SiderAgenda value={currentDate} onSelect={setCurrentDate} />
            </div>
          </Sider>
        </Layout>
        <Modal
          title={modalTitle}
          width={'80%'}
          destroyOnClose={true}
          visible={openLocks}
          onCancel={() => setOpenLocks(!openLocks)}
          cancelButtonProps={{ style: { display: 'none' } }}
          okButtonProps={{ style: { display: 'none' } }}
        >
          {child}
        </Modal>
      </Layout>
    </>
  );
};
